import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Linkedin, ArrowUpRight, Loader2, CheckCircle2 } from "lucide-react";
import Reveal from "@/components/site/Reveal";
import { API, SITE } from "@/lib/site";
import usePageMeta from "@/lib/usePageMeta";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const PREFERRED_TIMES = [
  { value: "mornings", label: "Mornings (Mon–Fri)" },
  { value: "afternoons", label: "Afternoons (Mon–Fri)" },
  { value: "evenings", label: "Evenings" },
  { value: "weekends", label: "Weekends" },
  { value: "flexible", label: "Flexible — propose a time" },
];

const INITIAL = {
  name: "",
  email: "",
  company: "",
  role: "",
  challenge: "",
  preferred_time: "",
};

export default function ContactPage() {
  usePageMeta({
    title: "Contact",
    description:
      "Start with a short, confidential discovery call — no pitch, no obligation. Reach out to Anna Zavarzina about your transformation challenge.",
    path: "/contact",
  });
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.challenge.trim()) {
      toast.error("Please fill in your name, email, and current challenge.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSubmitted(true);
      setForm(INITIAL);
      toast.success("Thank you — your message has been received.");
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "Something went wrong. Please try again, or email us directly.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-contact">
      {/* Header */}
      <section data-testid="contact-header">
        <div className="container-tight section-pad">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-8">
              <Reveal>
                <p className="overline">Contact</p>
                <h1
                  data-testid="contact-heading"
                  className="mt-6 font-display text-4xl sm:text-5xl lg:text-[72px] leading-[1.02] tracking-[-0.02em] text-brand-ink text-balance"
                >
                  Start with a short, focused conversation.
                </h1>
              </Reveal>
              <Reveal delay={0.05}>
                <p className="mt-8 text-lg md:text-xl text-brand-muted leading-relaxed max-w-2xl">
                  The first step is always a brief discovery call — confidential, no pitch, no obligation. Share a little about where you are, and I'll be in touch within two business days.
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* Form + sidebar */}
      <section className="bg-brand-surface2" data-testid="contact-body">
        <div className="container-tight section-pad">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            {/* Form */}
            <div className="lg:col-span-8">
              <Reveal>
                <div className="bg-white border border-brand-line p-8 md:p-12">
                  {submitted ? (
                    <div data-testid="contact-success" className="py-10 flex flex-col items-start">
                      <CheckCircle2 className="h-12 w-12 text-brand-accent" strokeWidth={1.25} />
                      <h2 className="mt-6 font-display text-3xl tracking-tight text-brand-ink">
                        Thank you — message received.
                      </h2>
                      <p className="mt-4 text-base text-brand-muted leading-relaxed max-w-lg">
                        I'll review your note personally and reply within two business days to propose a time for a discovery call.
                      </p>
                      <button
                        type="button"
                        onClick={() => setSubmitted(false)}
                        data-testid="contact-send-another"
                        className="btn-ghost mt-10"
                      >
                        Send another message
                      </button>
                    </div>
                  ) : (
                    <form
                      onSubmit={onSubmit}
                      data-testid="contact-form"
                      className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-7"
                      noValidate
                    >
                      <div className="sm:col-span-1">
                        <label htmlFor="c-name" className="field-label">Full Name *</label>
                        <input
                          id="c-name"
                          data-testid="contact-input-name"
                          type="text"
                          value={form.name}
                          onChange={update("name")}
                          required
                          autoComplete="name"
                          className="input-box"
                          placeholder="Jane Doe"
                        />
                      </div>
                      <div className="sm:col-span-1">
                        <label htmlFor="c-email" className="field-label">Email *</label>
                        <input
                          id="c-email"
                          data-testid="contact-input-email"
                          type="email"
                          value={form.email}
                          onChange={update("email")}
                          required
                          autoComplete="email"
                          className="input-box"
                          placeholder="jane@company.com"
                        />
                      </div>
                      <div className="sm:col-span-1">
                        <label htmlFor="c-company" className="field-label">Company</label>
                        <input
                          id="c-company"
                          data-testid="contact-input-company"
                          type="text"
                          value={form.company}
                          onChange={update("company")}
                          autoComplete="organization"
                          className="input-box"
                          placeholder="Company name"
                        />
                      </div>
                      <div className="sm:col-span-1">
                        <label htmlFor="c-role" className="field-label">Role / Title</label>
                        <input
                          id="c-role"
                          data-testid="contact-input-role"
                          type="text"
                          value={form.role}
                          onChange={update("role")}
                          autoComplete="organization-title"
                          className="input-box"
                          placeholder="VP of Delivery"
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <label htmlFor="c-challenge" className="field-label">Current Challenge *</label>
                        <textarea
                          id="c-challenge"
                          data-testid="contact-input-challenge"
                          value={form.challenge}
                          onChange={update("challenge")}
                          required
                          rows={5}
                          className="input-box resize-none"
                          placeholder="A few sentences on where you are and what you're trying to move forward."
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <label className="field-label">Preferred Time for a Call</label>
                        <Select
                          value={form.preferred_time}
                          onValueChange={(v) => setForm((f) => ({ ...f, preferred_time: v }))}
                        >
                          <SelectTrigger
                            data-testid="contact-select-time"
                            className="w-full bg-white border border-brand-line h-auto px-4 py-3.5 text-base text-brand-ink rounded-none focus:ring-1 focus:ring-brand-accent focus:border-brand-accent"
                          >
                            <SelectValue placeholder="Select a window (optional)" />
                          </SelectTrigger>
                          <SelectContent className="rounded-none">
                            {PREFERRED_TIMES.map((t) => (
                              <SelectItem key={t.value} value={t.value} data-testid={`contact-time-${t.value}`}>
                                {t.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="sm:col-span-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6 pt-4">
                        <p className="text-xs text-brand-subtle leading-relaxed max-w-sm">
                          By submitting, you agree to be contacted about your inquiry. Your information stays confidential.
                        </p>
                        <button
                          type="submit"
                          disabled={submitting}
                          data-testid="contact-submit-button"
                          className="btn-primary min-w-[220px] disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          {submitting ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
                              Sending…
                            </>
                          ) : (
                            <>
                              Request Discovery Call
                              <ArrowUpRight className="h-4 w-4" strokeWidth={1.5} />
                            </>
                          )}
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </Reveal>
            </div>

            {/* Sidebar */}
            <aside className="lg:col-span-4 space-y-10" data-testid="contact-sidebar">
              <Reveal delay={0.1}>
                <div>
                  <p className="overline">Direct contact</p>
                  <p className="mt-5 text-base text-brand-muted leading-relaxed">
                    Prefer email? Reach out directly — I read every message personally.
                  </p>
                  <ul className="mt-8 space-y-5">
                    <li>
                      <a
                        href={`mailto:${SITE.email}`}
                        data-testid="contact-email-link"
                        className="group flex items-start gap-4 text-brand-ink hover:text-brand-accent transition-colors"
                      >
                        <span className="mt-0.5 inline-flex h-9 w-9 items-center justify-center border border-brand-line group-hover:border-brand-accent transition-colors">
                          <Mail className="h-4 w-4" strokeWidth={1.5} />
                        </span>
                        <span>
                          <span className="block text-xs tracking-[0.18em] uppercase text-brand-muted">Email</span>
                          <span className="block text-base mt-0.5 break-all">{SITE.email}</span>
                        </span>
                      </a>
                    </li>
                    <li>
                      <a
                        href={SITE.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="contact-linkedin-link"
                        className="group flex items-start gap-4 text-brand-ink hover:text-brand-accent transition-colors"
                      >
                        <span className="mt-0.5 inline-flex h-9 w-9 items-center justify-center border border-brand-line group-hover:border-brand-accent transition-colors">
                          <Linkedin className="h-4 w-4" strokeWidth={1.5} />
                        </span>
                        <span>
                          <span className="block text-xs tracking-[0.18em] uppercase text-brand-muted">LinkedIn</span>
                          <span className="block text-base mt-0.5">linkedin.com/in/anna-zavarzina-spc6</span>
                        </span>
                      </a>
                    </li>
                  </ul>
                </div>
              </Reveal>

              <Reveal delay={0.15}>
                <div className="border-l-2 border-brand-accent pl-6">
                  <p className="overline">What happens next</p>
                  <ol className="mt-5 space-y-4 text-[15px] text-brand-muted leading-relaxed">
                    <li className="flex gap-3"><span className="font-display font-semibold text-brand-ink">1.</span> I review your note personally — usually within one business day.</li>
                    <li className="flex gap-3"><span className="font-display font-semibold text-brand-ink">2.</span> We schedule a 30-minute discovery call at a time that works for you.</li>
                    <li className="flex gap-3"><span className="font-display font-semibold text-brand-ink">3.</span> We talk plainly — no pitch — and explore whether and how I can help.</li>
                  </ol>
                </div>
              </Reveal>
            </aside>
          </div>
        </div>
      </section>
    </div>
  );
}
